import { Box, Button, HStack, Icon, Input, Text } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { LuArrowLeft, LuSearch } from 'react-icons/lu';

import { ADVISOR_PANEL_NEUTRAL_100, ADVISOR_PANEL_NEUTRAL_250, ADVISOR_PANEL_PRIMARY_TEXT } from './advisor-panel-tokens';
import {
  AdvisorThreadHistoryList,
  type AdvisorHistoryThreadRow,
  type AdvisorThreadHistoryRowActionsMode,
} from './advisor-thread-history-list';

export type AdvisorThreadHistoryViewTopBarProps = {
  onBack: () => void;
  onNewChat: () => void;
  newChatDisabled?: boolean;
    title?: string;
};

export type AdvisorThreadHistoryViewProps = {
  threads: AdvisorHistoryThreadRow[];
  loading: boolean;
  errorText?: string | null;
  onSelectThread: (row: AdvisorHistoryThreadRow) => void;
  onDeleteThread?: (row: AdvisorHistoryThreadRow) => void | Promise<void>;
  deletingThreadId?: string | null;
  /** Default: ``hover-kebab-menu`` (Figma); use ``always-visible-trash`` for a legacy always-on delete control. */
  rowActionsMode?: AdvisorThreadHistoryRowActionsMode;
    showSearch?: boolean;
  /**
 * When set, render the Kin-style top bar (Back, title, New Chat). Omit when an embedder supplies its own
 * header and only needs search + list.
 */
  topBar?: AdvisorThreadHistoryViewTopBarProps;
  /**
 * When true, the view fills available height in a flex parent (full-panel chat history in org and partner).
 * Default true.
 */
  fillHeight?: boolean;
};

const DEFAULT_ROW_MODE: AdvisorThreadHistoryRowActionsMode = 'hover-kebab-menu';

/**
 * Search + thread list, optionally with the Kin **Chat History** top bar. Single implementation for core + partner
 * so row behavior (kebab, delete, hover handling) is not forked in app trees.
 */
export function AdvisorThreadHistoryView({
  threads,
  loading,
  errorText,
  onSelectThread,
  onDeleteThread,
  deletingThreadId,
  rowActionsMode = DEFAULT_ROW_MODE,
  showSearch = true,
  topBar,
  fillHeight = true,
}: AdvisorThreadHistoryViewProps) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return threads;
    }
    return threads.filter(t => (t.title ?? '').toLowerCase().includes(q));
  }, [threads, query]);

  const listThreads = showSearch ? filtered : threads;
  const emptyLabel =
    showSearch && query.trim() ? 'No conversations match your search.' : 'No past conversations yet.';

  const searchAndList = (
    <Box flex={fillHeight ? 1 : undefined} minH={fillHeight ? 0 : undefined} overflowY="auto" px={4} pt={3} pb={4}>
      {showSearch ? (
        <Box position="relative" mb={2}>
          <Icon
            as={LuSearch}
            boxSize={4}
            color={ADVISOR_PANEL_NEUTRAL_250}
            position="absolute"
            left="10px"
            top="50%"
            transform="translateY(-50%)"
            pointerEvents="none"
          />
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search"
            aria-label="Search chat history"
            h="32px"
            pl="32px"
            fontSize="sm"
            borderColor={ADVISOR_PANEL_NEUTRAL_100}
            _placeholder={{ color: ADVISOR_PANEL_NEUTRAL_250 }}
            _focusVisible={{
              borderColor: ADVISOR_PANEL_PRIMARY_TEXT,
              boxShadow: `0 0 0 1px ${ADVISOR_PANEL_PRIMARY_TEXT}`,
            }}
          />
        </Box>
      ) : null}
      <AdvisorThreadHistoryList
        rowActionsMode={rowActionsMode}
        threads={listThreads}
        loading={loading}
        errorText={errorText ?? null}
        onSelectThread={onSelectThread}
        onDeleteThread={onDeleteThread}
        deletingThreadId={deletingThreadId}
        emptyLabel={emptyLabel}
      />
    </Box>
  );

  if (topBar) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        flex={fillHeight ? 1 : undefined}
        minH={fillHeight ? 0 : undefined}
        w="100%"
      >
        <AdvisorThreadHistoryViewTopBar
          onBack={topBar.onBack}
          onNewChat={topBar.onNewChat}
          newChatDisabled={topBar.newChatDisabled}
          title={topBar.title}
        />
        {searchAndList}
      </Box>
    );
  }

  return searchAndList;
}

/** Figma **Chat History** top bar: ``← Back``, centered title, outlined **New Chat** (Kintsugi Advisor / Dashboard). */
export function AdvisorThreadHistoryViewTopBar({
  onBack,
  onNewChat,
  newChatDisabled,
  title = 'Chat History',
}: AdvisorThreadHistoryViewTopBarProps) {
  return (
    <HStack
      position="relative"
      px={4}
      h="64px"
      minH="64px"
      borderBottomWidth="1px"
      borderBottomColor={ADVISOR_PANEL_NEUTRAL_100}
      justify="space-between"
      bg="white"
      flexShrink={0}
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onBack}
        color="gray.800"
        px={1}
        fontWeight="normal"
        _hover={{ bg: 'gray.50' }}
      >
        <Icon as={LuArrowLeft} boxSize={4} mr={1} />
        Back
      </Button>
      <Text
        position="absolute"
        left="50%"
        transform="translateX(-50%)"
        fontWeight="medium"
        fontSize="14px"
        lineHeight="20px"
        color="black"
        pointerEvents="none"
      >
        {title}
      </Text>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onNewChat}
        disabled={newChatDisabled}
        borderColor={ADVISOR_PANEL_PRIMARY_TEXT}
        color={ADVISOR_PANEL_PRIMARY_TEXT}
        h="32px"
        minH="32px"
        px={3}
        fontWeight="medium"
        _hover={{ bg: 'teal.50' }}
      >
        New Chat
      </Button>
    </HStack>
  );
}

export const AgentThreadHistoryView = AdvisorThreadHistoryView;
export const AgentThreadHistoryViewTopBar = AdvisorThreadHistoryViewTopBar;
export type AgentThreadHistoryViewProps = AdvisorThreadHistoryViewProps;
export type AgentThreadHistoryViewTopBarProps = AdvisorThreadHistoryViewTopBarProps;
